import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { GenericChart, colors, spacing, LoadingDots } from '@retailer-platform/shared-components'
import { trackEvent } from 'utils/events/trackEvent'
import { insightsColors } from 'domains/insights-portal/utils/insight-colors'
import { InsightsMetricsTrendMetric } from 'domains/insights-portal/widgets/metrics-trend/InsightsMetricsTrendMetric'
import { type KpiTrendLineProps } from 'domains/insights-portal/widgets/KPIOverviewTrend/utils/KpiOverviewTrend.types'
import { InsightsPortalMetrics } from './InsightsPortalMetrics'
import { InsightsPortalKpiComparisonTooltip } from './InsightsPortalKpiComparisonTooltip'

const KpiMetricTrendFillContainer = styled.div({
  display: 'flex',
  gap: spacing.X24,
})

const LoadingDotsContainer = styled.div({
  height: '400px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

export const KpiTrendFill: FunctionComponent<KpiTrendLineProps> = ({
  analyticTrackingData,
  comparisonColor,
  benchmarkColor,
  comparisonSeriesName,
  setSelectedMetric,
  selectedMetric,
  isLoading,
  listOfMetricItemsWithData,
  primaryData,
  primaryColor,
  comparisonData,
  benchmarkData,
  selectedMetricName,
  selectedMetricFormatter,
  isLoadingMultiGroupData,
}) => {
  const selectedMetricWithData = listOfMetricItemsWithData.find(
    metricItem => metricItem.apiMetric === selectedMetric
  )

  const additionalSeries = [
    {
      data: comparisonData,
      color: comparisonColor,
      seriesName: comparisonSeriesName,
      dashType: 'Solid',
    },
    ...(benchmarkData.some(benchmarkDataItem => benchmarkDataItem.y !== undefined)
      ? [
          {
            data: benchmarkData,
            color: benchmarkColor,
          },
        ]
      : []),
  ]
  return (
    <>
      <KpiMetricTrendFillContainer>
        <div css={{ width: '50%' }}>
          <InsightsPortalMetrics
            layoutVariant="column"
            onClick={activeMetric => {
              trackEvent({
                id: `insights_portal.metric_card.changed`,
                description: `Selected a metric card`,
                data: {
                  metric: activeMetric,
                  widgetName: 'kpiOverview',
                  widgetId: `kpi-overview`,
                  dashboardName: analyticTrackingData?.dashboard,
                },
              })
              setSelectedMetric(activeMetric)
            }}
            isLoading={isLoading}
            selectedMetric={selectedMetric}
            metricInfoWithData={listOfMetricItemsWithData}
          />
        </div>
        <div
          css={{
            width: '50%',
            height: 304,
            border: `1px solid ${colors.GRAYSCALE.X20}`,
            borderRadius: 12,
          }}
        >
          <div style={{ top: 20, left: 20, position: 'relative' }}>
            <InsightsMetricsTrendMetric
              title={selectedMetricWithData.metricName}
              isLoading={isLoading}
              tooltip={selectedMetricWithData.tooltip}
              value={selectedMetricWithData.value}
              valueFormatFn={selectedMetricFormatter}
              color={null}
              comparisonValue={selectedMetricWithData.comparisonValue}
              desiredDirection={selectedMetricWithData.desiredDirection}
            />
          </div>
          {isLoadingMultiGroupData ? (
            <LoadingDotsContainer>
              <LoadingDots />
            </LoadingDotsContainer>
          ) : (
            <GenericChart
              seriesName={''}
              data={primaryData}
              containerStyle={{
                height: '90%',
                marginTop: `-${spacing.X12}`,
              }}
              showInLegend={false}
              color={primaryColor}
              showExportMenu={false}
              primaryDataFillColor={insightsColors.skyBlue}
              optionProps={{
                yAxis: {
                  gridLineWidth: 0,
                  visible: false,
                },
                xAxis: {
                  type: 'datetime',
                  lineWidth: 0,
                  tickLength: 0,
                  title: {
                    text: '',
                  },
                  ordinal: false,
                  labels: {
                    format: '{value:%b %e, %Y}',
                  },
                  visible: true,
                },
                plotOptions: {
                  series: {
                    marker: {
                      enabled: false,
                    },
                    dataLabels: {
                      enabled: false,
                    },
                  },
                },
                tooltip: {
                  enabled: true,
                },
                series: additionalSeries.map(series => ({
                  showInLegend: false,
                  type: 'line',
                  name: 'test',
                  color: series.color,
                  data: series.data,
                  label: { enabled: false },
                  lineWidth: 0,
                  lineColor: 'transparent',
                  marker: {
                    enabled: false,
                    radius: 0,
                    style: { display: 'none' },
                  },
                  states: {
                    hover: {
                      lineWidth: 0,
                      halo: {
                        size: 0,
                      },
                      marker: {
                        states: {
                          hover: {
                            color: 'red',
                            enabled: false,
                          },
                        },
                        enabled: false,
                        radius: 0,
                        style: { display: 'none' },
                        fillColor: 'transparent',
                      },
                    },
                  },
                })),
                chart: {
                  marginTop: 20,
                  marginRight: 20,
                  marginLeft: 20,
                  backgroundColor: 'transparent',
                },
              }}
              CustomTooltip={formatterContext => (
                <InsightsPortalKpiComparisonTooltip
                  comparisonData={comparisonData}
                  primaryData={primaryData}
                  metricFormatter={selectedMetricFormatter}
                  formatterContext={formatterContext}
                  selectedMetricName={selectedMetricName}
                  benchmarkData={benchmarkData}
                />
              )}
            />
          )}
        </div>
      </KpiMetricTrendFillContainer>
    </>
  )
}
